import { Injectable } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { Observable, from, lastValueFrom } from 'rxjs';
import { map, tap, mergeMap } from 'rxjs/operators';
import { CookieService } from 'ngx-cookie-service';
import { SecureStorageService } from '@app/services/secure-storage.service';
import { environment } from 'environments/environment';
import { PermissionsService } from '@app/services/permissions.service';

export interface AdminLoginPayload {
  email: string;
  password: string;
  provider?: string;
  recaptcha?: string;
  loggedInDeviceLocation?: string;
  loginDevice?: any;
}

export interface AdminForgotPasswordPayload {
  email: string;
  recaptcha?: string;
}

export interface AdminResetPasswordPayload {
  token: string;
  password: string;
  c_password?: string; // match API naming
  recaptcha?: string;
}

@Injectable({ providedIn: 'root' })
export class AdminAuthService {
  private base = `${environment.apiUrl}admin/auth/`;
  private readonly httpWithoutInterceptor: HttpClient;

  constructor(
    private http: HttpClient,
    httpBackend: HttpBackend,
    private cookieService: CookieService,
    private secureStorage: SecureStorageService,
    private permissionsService: PermissionsService
  ) {
    this.httpWithoutInterceptor = new HttpClient(httpBackend);
  }

  login(payload: AdminLoginPayload): Observable<any> {
    const url = `${this.base}login`;
    return this.http.post<any>(url, { payload }).pipe(
      mergeMap((res) =>
        from(
          (async () => {
            try {
              const access = res?.data?.access?.access_token;
              const refresh = res?.data?.access?.refresh_token;
              const adminID = res?.data?.adminID ?? res?.data?.userID;
              const timezone = this.extractTimezone(res);
              if (access) {
                this.cookieService.set('admin_access_token', access, undefined, '/');
                // ensure encryption key exists before persisting securely
                try {
                  await this.secureStorage.setSecretKey(adminID || '', res?.data?.email || '', true);
                } catch (e) {
                  // ignore setSecretKey failures
                }
                await this.secureStorage.setItem('admin_access_token', access);
              }
              if (refresh) {
                this.cookieService.set('admin_refresh_token', refresh, undefined, '/');
                await this.secureStorage.setItem('admin_refresh_token', refresh);
              }

              if (adminID !== undefined && adminID !== null) {
                localStorage.setItem('adminID', String(adminID));
              }
              if (res?.data?.pmLevel !== undefined && res?.data?.pmLevel !== null) {
                localStorage.setItem('adminPmLevel', String(res.data.pmLevel));
              }

              const profile = {
                adminID,
                pmLevel: res?.data?.pmLevel,
                email: res?.data?.email,
                name: res?.data?.name,
                timezone,
                // preserve any photo/avatar if supplied
                avatar: res?.data?.avatar || res?.data?.photoUrl || null,
              };
              await this.secureStorage.setItem('admin_profile', JSON.stringify(profile));
              if (timezone) {
                await this.secureStorage.setItem('effectiveTimezone', timezone);
                await this.secureStorage.setItem('timezone', timezone);
              }

              const permissions = res?.data?.permissions ?? res?.data?.access?.permissions;
              if (permissions) {
                this.permissionsService.setAll(permissions);
              }
            } catch (e) {
              console.warn('Failed to persist admin auth data', e);
            }
            return res;
          })()
        )
      )
    );
  }

  forgotPassword(payload: AdminForgotPasswordPayload): Observable<any> {
    const url = `${this.base}reset_password`;
    return this.http.post<any>(url, { payload });
  }

  validateToken(token: string): Observable<any> {
    const url = `${this.base}password_reset/validate_token`;
    return this.http.post<any>(url, { payload: { token } });
  }

  resetPassword(payload: AdminResetPasswordPayload): Observable<any> {
    const url = `${this.base}password_reset`;
    // API expects c_password sometimes; ensure both fields exist
    const body: any = { payload: { ...payload } };
    if (!body.payload.c_password && body.payload.password) {
      body.payload.c_password = body.payload.password;
    }
    return this.http.post<any>(url, body);
  }

  refreshToken(refreshToken?: string): Observable<any> {
    const url = `${this.base}refresh-token`;
    const payload = refreshToken ? { refreshToken } : { refreshToken: this.cookieService.get('admin_refresh_token') };
    return this.httpWithoutInterceptor.post<any>(url, { payload }).pipe(
      mergeMap((res) =>
        from(
          (async () => {
            try {
              const access = res?.data?.access?.access_token;
              const refresh = res?.data?.access?.refresh_token;
              const timezone = this.extractTimezone(res);
              if (access) {
                this.cookieService.set('admin_access_token', access, undefined, '/');
                await this.secureStorage.setItem('admin_access_token', access);
              }
              if (refresh) {
                this.cookieService.set('admin_refresh_token', refresh, undefined, '/');
                await this.secureStorage.setItem('admin_refresh_token', refresh);
              }

              if (timezone) {
                await this.secureStorage.setItem('effectiveTimezone', timezone);
                await this.secureStorage.setItem('timezone', timezone);
              }

              if (res?.data?.pmLevel !== undefined && res?.data?.pmLevel !== null) {
                localStorage.setItem('adminPmLevel', String(res.data.pmLevel));
              }

              const permissions = res?.data?.permissions ?? res?.data?.access?.permissions;
              if (permissions) {
                this.permissionsService.setAll(permissions);
              }
            } catch (e) {
              console.warn('Failed to persist refreshed admin tokens', e);
            }
            return res;
          })()
        )
      )
    );
  }

  async logout(): Promise<void> {
    const url = `${this.base}logout`;
    try {
      // call backend logout but don't fail the client flow if backend errors
      try {
        await lastValueFrom(this.http.post<any>(url, {}));
      } catch (e) {
        // ignore backend logout errors
      }

      await this.clearLocalSession();
    } catch (e) {
      // ignore
    }
  }

  /** Clear only the local Admin session without making another API request. */
  async clearLocalSession(): Promise<void> {
    try {
      await this.secureStorage.clearStorage(true);
    } catch (e) {
      // Continue clearing the non-encrypted session values.
    }
    this.cookieService.delete('admin_access_token', '/');
    this.cookieService.delete('admin_refresh_token', '/');
    localStorage.removeItem('adminID');
    localStorage.removeItem('adminPmLevel');
  }

  private extractTimezone(response: any): string {
    const timezone =
      response?.data?.effectiveTimezone ||
      response?.data?.timezone ||
      response?.data?.timeZone ||
      '';
    return typeof timezone === 'string' ? timezone.trim() : '';
  }
}
